import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthServiceTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import {
  GROWTH_SERVICE_CHANNELS,
  GROWTH_SERVICE_TRIGGER_TYPES,
  emptyPlaybookFormJson,
  growthRetentionPlaybookFields,
  playbookApiJsonToFormJson,
  playbookFormJsonToApiJson
} from './growth-service-form.models';
import { GrowthRetentionPlay, GrowthServiceService } from './growth-service.service';

@Component({
  standalone: true,
  selector: 'ain-growth-retention-play-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    DialogModule,
    PartySelectorComponent,
    StructuredJsonFormComponent
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('growth.service.retentionPlayDetail.form.createTitle') : t('growth.service.retentionPlayDetail.form.editTitle')"
      [description]="t('growth.service.retentionPlayDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.service.retentionPlayDetail.help.title'"
        [descriptionKey]="'growth.service.retentionPlayDetail.help.description'"
        [bulletKeys]="['growth.service.retentionPlayDetail.help.bullets.consent', 'growth.service.retentionPlayDetail.help.bullets.approval']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/service/retention-plays']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            @if (!isCreateMode() && isDraft()) {
              <button
                pButton
                type="button"
                [label]="t('growth.service.retentionPlayDetail.actions.approve')"
                severity="secondary"
                [disabled]="approving()"
                (click)="openApprovalDialog(true)"></button>
              <button
                pButton
                type="button"
                [label]="t('growth.service.retentionPlayDetail.actions.reject')"
                severity="danger"
                [disabled]="approving()"
                (click)="openApprovalDialog(false)"></button>
            }
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
            }
          </div>
        </div>

        <section class="card">
          <h3>{{ t('growth.service.retentionPlayDetail.sections.details') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.service.retentionPlayDetail.help.details.title'"
            [descriptionKey]="'growth.service.retentionPlayDetail.help.details.description'" />
          <div class="grid">
            @if (isCreateMode()) {
              <label class="wide">
                <span>{{ t('growth.service.retentionPlayDetail.fields.partyId') }}</span>
                <ain-party-selector [(ngModel)]="form.partyId" />
              </label>
            } @else {
              <label class="wide">
                <span>{{ t('growth.service.retentionPlayDetail.fields.partyId') }}</span>
                <ain-party-selector [ngModel]="form.partyId" [disabled]="true" />
              </label>
            }
            <label>
              <span>{{ t('growth.service.retentionPlayDetail.fields.triggerType') }}</span>
              <p-select
                class="field-select"
                [options]="triggerTypeOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="form.triggerType"
                [disabled]="!isCreateMode()"
                appendTo="body" />
            </label>
            <label>
              <span>{{ t('growth.service.retentionPlayDetail.fields.channelHint') }}</span>
              <p-select
                class="field-select"
                [options]="channelOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="form.channelHint"
                [disabled]="!isCreateMode()"
                appendTo="body" />
            </label>
            @if (!isCreateMode()) {
              <label>
                <span>{{ t('growth.service.retentionPlayDetail.fields.approvalState') }}</span>
                <input
                  pInputText
                  type="text"
                  [ngModel]="approvalStateLabel(play()?.approvalState)"
                  [disabled]="true" />
              </label>
              <label>
                <span>{{ t('growth.service.retentionPlayDetail.fields.consentChecked') }}</span>
                <input pInputText type="text" [ngModel]="play()?.consentChecked ? t('common.yes') : t('common.no')" [disabled]="true" />
              </label>
              @if (play()?.consentBlockReason) {
                <label class="wide">
                  <span>{{ t('growth.service.retentionPlayDetail.fields.consentBlockReason') }}</span>
                  <input pInputText type="text" [ngModel]="play()!.consentBlockReason" [disabled]="true" />
                </label>
              }
              @if (play()?.approvalReason) {
                <label class="wide">
                  <span>{{ t('growth.service.retentionPlayDetail.fields.approvalReason') }}</span>
                  <textarea pInputTextarea rows="3" [ngModel]="play()!.approvalReason" [disabled]="true"></textarea>
                </label>
              }
            }
          </div>
        </section>

        <section class="card">
          <h3>{{ t('growth.service.retentionPlayDetail.sections.playbook') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.service.retentionPlayDetail.help.playbook.title'"
            [descriptionKey]="'growth.service.retentionPlayDetail.help.playbook.description'" />
          <ain-structured-json-form
            [fields]="playbookFields"
            [(payloadJson)]="playbookFormJson"
            [readonly]="!isCreateMode()" />
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }

      <p-dialog
        [header]="approvalDialogApprove() ? t('growth.service.retentionPlayDetail.approvalDialog.approveTitle') : t('growth.service.retentionPlayDetail.approvalDialog.rejectTitle')"
        [modal]="true"
        [(visible)]="approvalDialogVisible"
        [style]="{ width: 'min(560px, 95vw)' }">
        <p class="dialog-copy">
          {{ approvalDialogApprove() ? t('growth.service.retentionPlayDetail.approvalDialog.approveMessage') : t('growth.service.retentionPlayDetail.approvalDialog.rejectMessage') }}
        </p>
        <label class="dialog-field">
          <span>{{ t('growth.service.retentionPlayDetail.approvalDialog.reasonLabel') }}</span>
          <textarea pInputTextarea rows="4" [(ngModel)]="approvalReason"></textarea>
          @if (!approvalDialogApprove()) {
            <small class="hint">{{ t('growth.service.retentionPlayDetail.approvalDialog.reasonRequired') }}</small>
          }
        </label>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="approvalDialogVisible = false"></button>
          <button
            pButton
            type="button"
            [label]="approvalDialogApprove() ? t('growth.service.retentionPlayDetail.actions.approve') : t('growth.service.retentionPlayDetail.actions.reject')"
            [loading]="approving()"
            (click)="submitApproval()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: span 3; }
    label { display: grid; gap: 6px; }
    .field-select { width: 100%; }
    .message.error { color: #b91c1c; }
    .dialog-copy { margin: 0 0 12px; color: #334155; }
    .dialog-field { display: grid; gap: 6px; }
    .hint { color: #64748b; font-size: 12px; }
    @media (max-width: 1200px) {
      .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .wide { grid-column: span 2; }
    }
    @media (max-width: 760px) {
      .grid { grid-template-columns: 1fr; }
      .wide { grid-column: span 1; }
    }
  `]
})
export class GrowthRetentionPlayDetailComponent implements OnInit {
  private readonly api = inject(GrowthServiceService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly approving = signal(false);
  readonly errorMessage = signal('');
  readonly isCreateMode = signal(false);
  readonly play = signal<GrowthRetentionPlay | null>(null);
  readonly approvalDialogApprove = signal(true);

  readonly playbookFields = growthRetentionPlaybookFields;

  approvalDialogVisible = false;
  approvalReason = '';

  form = {
    partyId: null as string | null,
    triggerType: 'Manual',
    channelHint: 'email'
  };

  playbookFormJson = emptyPlaybookFormJson();

  private playId = '';
  private subscribedTopic: string | null = null;

  triggerTypeOptions(): Array<{ label: string; value: string }> {
    return GROWTH_SERVICE_TRIGGER_TYPES.map(value => ({
      value,
      label: this.t(`growth.service.retentionPlayDetail.triggerType.${value}`)
    }));
  }

  channelOptions(): Array<{ label: string; value: string }> {
    return GROWTH_SERVICE_CHANNELS.map(value => ({
      value,
      label: this.t(`growth.comms.channel.${value}`)
    }));
  }

  approvalStateLabel(state?: string | null): string {
    if (!state) {
      return '-';
    }
    return this.t(`growth.service.retentionPlays.approvalState.${state}`);
  }

  ngOnInit(): void {
    this.playId = this.route.snapshot.paramMap.get('playId') ?? '';
    this.isCreateMode.set(!this.playId || this.playId === 'new');

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthServiceChanged) {
            return false;
          }
          if (this.isCreateMode()) {
            return false;
          }
          const payloadPlayId = event.payload['retentionPlayId'];
          return payloadPlayId === undefined || String(payloadPlayId).toLowerCase() === this.playId.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();

    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }
    this.load(true);
  }

  isDraft(): boolean {
    return this.play()?.approvalState === 'Draft';
  }

  openApprovalDialog(approve: boolean): void {
    this.approvalDialogApprove.set(approve);
    this.approvalReason = '';
    this.errorMessage.set('');
    this.approvalDialogVisible = true;
  }

  load(showLoading = true): void {
    if (!this.playId || this.playId === 'new') {
      this.loading.set(false);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getRetentionPlay(this.playId).subscribe({
      next: row => {
        this.play.set(row);
        this.populateForm(row);
        this.loading.set(false);
      },
      error: () => {
        this.play.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.service.retentionPlayDetail.messages.loadFailed'));
      }
    });
  }

  save(): void {
    if (!this.form.partyId) {
      this.errorMessage.set(this.t('growth.service.retentionPlayDetail.messages.partyRequired'));
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');
    this.api.createRetentionPlay({
      partyId: this.form.partyId,
      triggerType: this.form.triggerType,
      channelHint: this.form.channelHint,
      playbookJson: playbookFormJsonToApiJson(this.playbookFormJson)
    }).subscribe({
      next: created => {
        this.saving.set(false);
        void this.router.navigate(['/growth/service/retention-plays', created.id]);
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('growth.service.retentionPlayDetail.messages.saveFailed'));
      }
    });
  }

  submitApproval(): void {
    const approve = this.approvalDialogApprove();
    const reason = this.approvalReason.trim();
    if (!approve && !reason) {
      this.errorMessage.set(this.t('growth.service.retentionPlayDetail.messages.reasonRequired'));
      return;
    }
    if (!this.playId || this.playId === 'new') {
      return;
    }

    this.approving.set(true);
    this.errorMessage.set('');
    this.api.approveRetentionPlay(this.playId, approve, reason || null).subscribe({
      next: row => {
        this.play.set(row);
        this.populateForm(row);
        this.approving.set(false);
        this.approvalDialogVisible = false;
      },
      error: () => {
        this.approving.set(false);
        this.errorMessage.set(this.t('growth.service.retentionPlayDetail.messages.approveFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private populateForm(row: GrowthRetentionPlay): void {
    this.form = {
      partyId: row.partyId,
      triggerType: row.triggerType || 'Manual',
      channelHint: row.channelHint || 'email'
    };
    this.playbookFormJson = playbookApiJsonToFormJson(row.playbookJson);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthServiceTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
